import dayjs from 'dayjs'
import React from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import ReactTooltip from 'react-tooltip'

import { getProductDetail } from '../../../api/senecApiUtils'
import { introImage, packC2GImage, packFFImage, packHeatImage } from '../../../assets'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setCustomerValuesIntroSlice } from '../../../store/slices/customerInputValuesSlice'
import { setProductDetailSlice } from '../../../store/slices/productDetailSlice'
import { customerValuesType } from '../../../types'
import { FormCheck, FormInput, FormSelect, FormDate, FormRadioItem } from '../../molecules'

type IntroProps = {
  nextPage: string,
  changeActivePage: (pageUrl: string) => void,
  getProposals: (inputs: any) => void,
}

const Component = ({ nextPage, changeActivePage, getProposals }: IntroProps) => {
  const customerInputValues = useAppSelector(state => state.customervalues)
  const { register, handleSubmit, control, watch, formState } = useForm<customerValuesType.Intro>({
    mode: 'onChange',
    defaultValues: { ...customerInputValues['p-intro'] }
  })

  const blackPostalCodes = useAppSelector(state => state.blackpostalcodes)

  const watchAdditionalInputsCloud = watch('intro-ctg-paketCloudToGo')
  const watchAdditionalInputsWaerme = watch('intro-wae-waermePaket')
  const watchAdditionalInputsFF = watch('intro-faf-paketFamilyAndFriends')
  const watchAdditionalInputsDetails = watch('intro-faf-2-details')
  const watchAdditionalInputsPvSystem = watch('pv-system')

  const dispatch = useAppDispatch()

  const validatePostalCode = (postalCode: string) => {
    return !blackPostalCodes.value.includes(postalCode)
  }

  const onSubmitFn: SubmitHandler<customerValuesType.Intro> = async values => {
    const submittedValues = {
      ...values
    }
    try {
      const data = await getProductDetail(values['senec-system-id'])
      if (data) {
        dispatch(setProductDetailSlice(data))
      } else {
        // In case we receive a no data mocked data is shown.
        // eslint-disable-next-line no-console
        console.log(
          'No data was received from the Product details endpoint.'
        )
      }
      getProposals(submittedValues)
      dispatch(setCustomerValuesIntroSlice(values))
      changeActivePage(nextPage)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  return (
    <div className="react-form__page react-form__page--intro">
      <div className="react-form__page__header relative">
        <div className="react-form__page__header__text flex flex-wrap">
          <div className="w-full sm:w-1/2 md:mb-10 md:pb-16 md:pt-16 md:pl-10">
            <img
              src={`/themes/custom/granite/react-form-app/build${introImage}`}
              className="img img-fluid"
              alt="introduction video thumbnail"
            />
          </div>
          <div className="w-full sm:w-1/2 sm:p-4 pb-10 md:px-6 md:p-10 md:pt-16 md:mt-0">
            <h3 className="mb-5 h3">Willkommen in der Angebotskalkulation der SENEC.Cloud</h3>
            <p>Berechnen Sie ganz einfach Ihren Cloud-Tarif und profitieren Sie von niedrigen monatlichen Stromkosten.
              Anschließend können Sie den Vertrag zur SENEC.Cloud direkt online digital abschließen.
            </p>
            <p>  Gerne stehen wir Ihnen bei Rückfragen unter der Rufnummer
              <a href="tel:+4934198982400" className="color-text no-underline">+49
                341 9898 2400</a> zur Verfügung.
            </p>
            <p>Hinweis: Die SENEC.Cloud kann nur in Verbindung mit einer Photovoltaik (PV)-Anlage und einem
              SENEC Stromspeicher gebucht werden. Andere Erzeugungsanlagen,
              beispielsweise Blockheizkraftwerke (BHKW), werden nicht unterstützt.</p>
          </div>
        </div>
      </div>
      <div className="react-form__page--intro__body">
        <form>
          <div className="bg-gray-lighter p-6 md:p-10 xl:p-16">
            <FormRadioItem
              name="pv-system"
              id="pv-system-installed"
              value="0"
              label="Ich habe bereits eine PV-Anlage sowie einen Stromspeicher und kenne meinen Jahresverbrauch sowie die Netzeinspeisung."
              className="inline-block"
              info="Wir berechnen Ihnen ein individuelles Cloud-Angebot anhand Ihrer Angaben."
              register={register}
              defaultChecked={false}
              errors={formState.errors}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
            />
            <FormRadioItem
              name="pv-system"
              id="pv-system-not-installed"
              value="1"
              label="Ich plane meine PV-Anlage sowie meinen Stromspeicher und kenne meinen zukünftigen Jahresverbrauch sowie meine zukünftige Netzeinspeisung noch nicht."
              className="inline-block"
              info="Wir berechnen Ihren zukünftigen Jahresverbrauch sowie Ihre Netzeinspeisung und erstellen Ihnen anhand dessen Ihr individuelles Cloud-Angebot."
              register={register}
              defaultChecked={true}
              errors={formState.errors}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
            />
            <h3 className="h3 mb-10">Angaben zu Haus und Speicher</h3>
            <div className="flex flex-wrap -mx-4">
              <FormInput
                id="customer-postleitzahl"
                className="w-full xl:w-4/12 px-4"
                placeholder="Postleitzahl"
                label="Postleitzahl"
                errors={formState.errors}
                register={register}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  pattern: {
                    value: /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/,
                    message: 'Bitte geben Sie eine fünfstellige Postleitzahl ein.'
                  },
                  validate: (postalCode: string) => validatePostalCode(postalCode) || 'Leider können wir die SENEC.Cloud in Ihrer Region nicht anbieten.'
                }}
              />
              <FormInput
                id="power-consumption-year-kwh"
                placeholder="Jahresverbrauch"
                label="Jahresverbrauch"
                measurement="kWh pro Jahr"
                className="w-full xl:w-4/12 px-4"
                errors={formState.errors}
                register={register}
                info={watchAdditionalInputsPvSystem == '0'
                  ? 'Wir berechnen Ihnen ein individuelles Cloud-Angebot anhand Ihrer Angaben.'
                  : 'Wir berechnen/ prognostizieren Ihren zukünftigen Jahresverbrauch sowie ihre Netzeinspeisung und berechnen Ihnen anhand dessen ihr individuelles Cloud-Angebot.'}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  pattern: {
                    value: /^(?:[0-9][0-9]{0,4}(?:,\d{1,2}|\.\d{0,2})?|99999|99999.9|99999.99)$/,
                    message: 'Leider können wir Ihnen für den angegebenen Hausstromverbrauch kein Angebot unterbreiten. Zulässiger Hausstromverbrauch: 1 - 99.999 kWh.”'
                  }
                }}
              />
              <FormSelect
                errors={formState.errors}
                id="senec-system-id"
                label="Stromspeicher"
                info="Zur Auswahl stehen derzeit lediglich Stromspeicher der Marke SENEC."
                className="w-full xl:w-4/12 px-4"
                register={register}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.'
                }}
                options={drupalSettings.app_block.products_ids}
              />
            </div>
            <h3 className="h3 mb-10">Angaben zur PV-Anlage</h3>
            <div className="flex flex-wrap custom-info-dropdown -mx-4">
              <FormInput
                id="pv-nominal-power-kwp"
                placeholder="Größe der PV-Anlage"
                label="Größe der PV-Anlage"
                measurement="kWp"
                className="w-full xl:w-4/12 px-4"
                info="Die Größe der PV-Anlage wird auch als Anlagenleistung bezeichnet, welche in kWp (Kilowattpeak) angegeben wird. Die Angabe über die Größe der PV-Anlage können Sie aus dem Kaufvertrag der Solaranlage, der Anmeldung an Ihren Verteilnetzbetreiber oder Ihrem Einspeisevertrag entnehmen. Sollten Sie sich unsicher sein, kann Ihnen der Anlagenverkäufer / Installateur eine Auskunft über die Installierte Leistung geben."
                errors={formState.errors}
                register={register}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  pattern: {
                    value: /^([1-9][0-9]?([.,]\d{1,4})?)$/,
                    message: 'Leider können wir Ihnen für die angegebene Größe Ihrer PV-Anlage kein Angebot unterbreiten. Zulässige Anlagengröße: 1 - 99 kWp.'
                  }
                }}
              />
              <FormDate
                errors={formState.errors}
                register={register}
                control={control}
                id="generator-anlage-inbetriebnahme"
                label="Inbetriebnahmedatum der PV-Anlage"
                className="w-full xl:w-4/12 px-4"
                info="Bitte geben Sie das Datum der Inbetriebnahme Ihrer PV-Anlage an.
              Für eine Inbetriebnahme, die 20 Jahre in der Vergangenheit liegt,
              ist die Förderung leider bereits ausgelaufen oder steht kurz vor dem Ende.
              Da die EEG-Vergütung ein wichtiger Bestandteil der SENEC.Cloud ist,
              kann für diese Anlagen leider kein Cloud-Vertrag abgeschlossen werden."
                peekNextMonth={true}
                showMonthDropdown={true}
                showYearDropdown={true}
                dropdownMode="select"
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  validate: value => {
                    const installationDate = dayjs().subtract(20, 'year').startOf('month')
                    return dayjs(value).isAfter(installationDate) || 'Leider können wir Ihnen für das angegebene Inbetriebnahme-Datum kein Angebot unterbreiten. Das Datum der Inbetriebnahme Ihrer PV-Anlage darf 20 Jahre nicht überschreiten.'
                  }
                }}
              />
              { watchAdditionalInputsPvSystem == '0' && (
                <FormInput
                  id="grid-feed-year-kwh"
                  placeholder="Netzeinspeisung"
                  label="Netzeinspeisung"
                  measurement="kWh pro Jahr"
                  className="w-full xl:w-4/12 px-4"
                  errors={formState.errors}
                  register={register}
                  info="Sofern Sie Ihre Netzeinspeisung pro Jahr kennen, können Sie uns diese hier mitteilen. Auf Grundlage dieser Netzeinspeisung werden wir Ihnen das Angebot kalkulieren."
                  validations={{
                    required: 'Bitte füllen Sie dieses Feld aus.',
                    pattern: {
                      value: /^(?:[0-9][0-9]{0,4}(?:,\d{1,2}|\.\d{0,2})?|99999|99999.9|99999.99)$/,
                      message: 'Leider können wir Ihnen für die angegebene Netzeinspeisung kein Angebot unterbreiten. Zulässige Höhe der Netzeinspeisung: 1 - 99.999 kWh pro Jahr.'
                    }
                  }}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center w-full">
            <div className="w-full sm:p-4 pb-10 md:px-6 md:p-10 md:pt-16 mt-6 md:mt-0">
              <h2 className="h2">SENEC.Cloud Zusatzprodukte</h2>
              <h5 className="h5 mb-lg-4">Für eine rundum unabhängige Stromversorgung</h5>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mx-2">
            <div className="flex flex-col py-4 px-4 bg-white">
              <img
                src={`/themes/custom/granite/react-form-app/build${packC2GImage}`}
                className="w-full mb-4"
                alt="intro placeholder"
              />
              <div style={{ fontSize: '2rem' }} className="text-center">SENEC.Cloud to go</div>
              <div className="w-full p-4 bg-white">
                <p>
                  Mit dem Zusatzprodukt Cloud to go können Sie Ihre Cloud-Einspeisung für die Beladung
                  Ihres Elektro- und/oder Hybrid-Fahrzeugs im größten Ladenetz Europas nutzen.
                </p>
                <a className="atv flex items-center"
                   target="_blank"
                   rel="noopener noreferrer"
                   href="https://senec.com/de/faq/senec-cloud/welche-zusatzprodukte-gibt-es-fuer-die-seneccloud">
                  Mehr Informationen zur SENEC.Cloud to go finden Sie in unserem FAQ
                </a>
              </div>
              <div className="w-full">
                <FormCheck
                  id="intro-ctg-paketCloudToGo"
                  label="Ja, ich möchte das Zusatzprodukt buchen."
                  className="mt-4"
                  register={register}
                  errors={formState.errors}
                />
              </div>
              <div className="w-full px-4">
                {watchAdditionalInputsCloud && (
                  <div className="flex flex-wrap -mx-4">
                    <FormInput
                      id="intro-ctg-annual-mileage"
                      label="Jährliche Fahrleistung"
                      placeholder="Jährliche Fahrleistung"
                      measurement="km"
                      info="Bitte geben Sie hier die jährliche Fahrleistung an, die Sie mit Ihrem Fahrzeug zurücklegen werden."
                      className="w-full"
                      inputType={'number'}
                      errors={formState.errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.',
                        pattern: {
                          value: /^(?:[1-9]\d{0,4}|0)$/,
                          message: 'Leider können wir Ihnen für die angegebenen jährliche Fahrleistung kein Angebot ' +
                            'unterbreiten. Ihre jährliche Fahrleistung darf 99.999 km nicht überschreiten.'
                        }
                      }}
                    />
                    <FormInput
                      id="intro-ctg-car-consumption"
                      label="Stromverbrauch des Autos"
                      placeholder="Stromverbrauch des Autos"
                      measurement="kWh/100km"
                      info="Bitte geben Sie hier den Stromverbrauch Ihres Fahrzeugs pro 100 km Fahrstrecke an."
                      className="w-full"
                      errors={formState.errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.',
                        pattern: {
                          value: /^(100(?:\.00?)?|\d?\d(?:,\d\d?|\.\d\d?)?)$/,
                          message: 'Leider können wir Ihnen für den angegebenen Stromverbrauch Ihres Autos kein ' +
                            'Angebot unterbreiten. Der angegebene Stromverbrauch darf 100 kWh/ 100 km nicht ' +
                            'überschreiten.'
                        }
                      }}
                    />
                    <FormInput
                      id="intro-ctg-at-home"
                      label="Zu Hause geladen"
                      placeholder="Zu Hause geladen"
                      measurement="%"
                      info="Bitte geben Sie hier an, zu welchem Anteil Sie Ihr Fahrzeug zu Hause laden werden."
                      className="w-full"
                      inputType={'number'}
                      errors={formState.errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.',
                        pattern: {
                          value: /^(?:[1-9][0-9]?|0)$/,
                          message: 'Bitte geben Sie den Anteil in % an, zu dem Sie Ihr Auto zu Hause laden. ' +
                            'Zulässiger Wert: 0-99%.'
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col py-4 px-4 bg-white">
            <img
                src={`/themes/custom/granite/react-form-app/build${packFFImage}`}
                className="w-full mb-4"
                alt="intro placeholder"
              />
              <div style={{ fontSize: '2rem' }} className="text-center">SENEC.Family & Friends</div>
              <div className="w-full p-4 bg-white">
                <p>
                  Mit dem Zusatzprodukt Family & Friends können Sie bis zu zwei weitere Lieferstellen versorgen,
                  beispielsweise das eigene Ferienhaus oder die Wohnung von Freunden und Familie.
                </p>
                <a className="atv flex items-center"
                   target="_blank"
                   rel="noopener noreferrer"
                   href="https://senec.com/de/faq/senec-cloud/welche-zusatzprodukte-gibt-es-fuer-die-seneccloud"
                >
                  Mehr Informationen zur SENEC.Family & Friends finden Sie in unserem FAQ
                </a>
              </div>
              <FormCheck
                id="intro-faf-paketFamilyAndFriends"
                label="Ja, ich möchte das Zusatzprodukt buchen."
                className="mt-4"
                register={register}
                errors={formState.errors}
              />
              <div className="w-full mt-4 px-4">
                {watchAdditionalInputsFF && (
                  <div className="flex flex-wrap">
                    <div className="w-full">
                      <FormCheck
                        id="intro-faf-1-details"
                        label="1. zusätzliche Abnahmestelle"
                        disabled={true}
                        className="w-full"
                        register={register}
                        errors={formState.errors}
                      />
                      <FormInput
                        id="intro-faf-1-postleitzahl"
                        placeholder="Postleitzahl"
                        label="Postleitzahl"
                        className="w-full"
                        errors={formState.errors}
                        register={register}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.',
                          pattern: {
                            value: /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/,
                            message: 'Bitte geben Sie eine fünfstellige Postleitzahl ein.'
                          },
                          validate: (postalCode: string) => validatePostalCode(postalCode) ||
                            'Leider können wir die SENEC.Cloud in Ihrer Region nicht anbieten.'
                        }}
                      />
                      <FormInput
                        id="intro-faf-1-vorjahresstromverbrauch"
                        label="Stromverbrauch in kWh"
                        measurement="kWh"
                        className="w-full"
                        errors={formState.errors}
                        register={register}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.',
                          pattern: {
                            value: /^(?:[0-9][0-9]{0,4}(?:,\d{1,2}|\.\d{0,2})?|99999|99999.9|99999.99)$/,
                            message: 'Leider können wir Ihnen für den angegebenen Stromverbrauch kein ' +
                              'Angebot unterbreiten. Zulässiger Stromverbrauch: 1 - 99.999 kWh.'
                          }
                        }}
                      />
                    </div>
                    <div className="w-full">
                      <FormCheck
                        id="intro-faf-2-details"
                        label="2. zusätzliche Abnahmestelle"
                        className="w-full"
                        register={register}
                        errors={formState.errors}
                      />
                      {watchAdditionalInputsDetails && (
                        <div>
                          <FormInput
                            id="intro-faf-2-postleitzahl"
                            placeholder="Postleitzahl"
                            label="Postleitzahl"
                            className="w-full"
                            errors={formState.errors}
                            register={register}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.',
                              pattern: {
                                value: /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/,
                                message: 'Bitte geben Sie eine fünfstellige Postleitzahl ein.'
                              },
                              validate: (postalCode: string) => validatePostalCode(postalCode) ||
                                'Leider können wir die SENEC.Cloud in Ihrer Region nicht anbieten.'
                            }}
                          />
                          <FormInput
                            id="intro-faf-2-vorjahresstromverbrauch"
                            label="Stromverbrauch in kWh"
                            measurement="kWh"
                            className="w-full"
                            errors={formState.errors}
                            register={register}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.',
                              pattern: {
                                value: /^(?:[0-9][0-9]{0,4}(?:,\d{1,2}|\.\d{0,2})?|99999|99999.9|99999.99)$/,
                                message: 'Leider können wir Ihnen für den angegebenen Stromverbrauch kein ' +
                                  'Angebot unterbreiten. Zulässiger Stromverbrauch : 1 - 99.999 kWh.'
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col py-4 px-4 bg-white">
            <img
                src={`/themes/custom/granite/react-form-app/build${packHeatImage}`}
                className="w-full mb-4"
                alt="intro placeholder"
              />
              <div style={{ fontSize: '2rem' }} className="text-center">SENEC.Wärme</div>
              <div className="w-full p-4 bg-white">
                <p>Mit dem Zusatzprodukt Wärme können Sie Ihren Wärmeerzeuger versorgen,
                  beispielsweise Ihre Wärmepumpe.
                  Voraussetzung ist, dass Ihr Wärmeerzeuger über einen separaten Zähler betrieben wird.
                </p>
                <a className="atv flex items-center"
                   target="_blank"
                   rel="noopener noreferrer"
                   href="https://senec.com/de/faq/senec-cloud/welche-zusatzprodukte-gibt-es-fuer-die-seneccloud">
                  Mehr Informationen zur SENEC.Wärme finden Sie in unserem FAQ
                </a>
              </div>
              <FormCheck
                id="intro-wae-waermePaket"
                label="Ja, ich möchte das Zusatzprodukt buchen."
                className="mt-4"
                register={register}
                errors={formState.errors}
              />
              <div className="w-full mt-4 px-4">
                {watchAdditionalInputsWaerme && (
                  <div className="flex flex-wrap">
                    <FormInput
                      id="intro-wae-vorjahresstromverbrauch"
                      label="Stromverbrauch in KWh"
                      measurement="kWh"
                      className="w-full"
                      errors={formState.errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.',
                        pattern: {
                          value: /^(?:[0-9][0-9]{0,4}(?:,\d{1,2}|\.\d{0,2})?|99999|99999.9|99999.99)$/,
                          message: 'Leider können wir Ihnen für den angegebenen Wärmestromverbrauch kein ' +
                            'Angebot unterbreiten. Zulässiger Wärmestromverbrauch: 1 - 99.999 kWh.'
                        }
                      }}
                    />
                    <div className='heat-pump-label-wrapper'>
                      <div className="ml-2">
                        <div className="form-group__info-icon">
                          <p>Haben Sie eine Wärmepumpe über 4,2 kW, die nach den 01.01.2024 in Betrieb genommen wurde?
                          <i
                            className="info-icon ml-4"
                            data-tip
                            data-for="bullet1"
                            data-place="bottom"
                            data-event="click focus"
                          /></p>
                        </div>
                        <ReactTooltip
                          id={'bullet1'}
                          effect="solid"
                          clickable={true}
                        >
                          <div className="form-group__info absolute active">
                            <p>Weitere Informationen zu steuerbaren Verbrauchseinrichtungen wie beispielsweise Wärmepumpen finden Sie unter <a className="link-white" href='https://senec.com/de/produkte/Chancen-%C2%A7-14a-EnWG#:~:text=Mit%20dem%20%C2%A7%2014a%20des,Last%20im%20Stromnetz%20zu%20optimieren' target='_blank' rel="noreferrer">SENEC und die Chancen von § 14a EnWG.</a></p>
                          </div>
                        </ReactTooltip>
                      </div>
                    </div>
                    <FormRadioItem
                      name="intro-wae-heat-pump"
                      id="intro-wae-heat-pump-installed"
                      value="1"
                      label="Ja"
                      className="w-full"
                      register={register}
                      defaultChecked={true}
                      errors={formState.errors}
                    />
                    <FormRadioItem
                      name="intro-wae-heat-pump"
                      id="intro-wae-heat-pump-not-installed"
                      value="0"
                      label="Nein"
                      className="w-full"
                      register={register}
                      defaultChecked={false}
                      errors={formState.errors}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="clearfix p-10 text-center">
            {nextPage && (
              <qz-button
                class="float-right"
                buttonType="submit"
                onClick={handleSubmit(onSubmitFn)}
              >
                Weiter
              </qz-button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Component
